import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, message } from 'antd';
import { FG140, g204Api } from '../../services/g204Api';
import { BatchAcceptanceBridge, pwg204f1AcceptanceManifest } from '../../features/acceptance';
import { useG204Mode } from './hooks/useG204Mode';
import { useLookupNames } from './hooks/useLookupNames';
import { useG204Record } from './hooks/useG204Record';
import { useG204Navigation } from './hooks/useG204Navigation';
import { useModulePermission } from '../../hooks/usePermission';
import PermissionGuard from '../../components/PermissionGuard';
import { Modules } from '../../constants/permissions';
import Tab1Header from './components/Tab1Header';
import Tab1DetailGrid from './components/Tab1DetailGrid';
import Tab2SubDetailGrid from './components/Tab2SubDetailGrid';
import ActionBar from './components/ActionBar';
import { getStatusBadge } from './helpers';
import './G204Form.css';

const G204Form: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);

  // Hooks
  const perms = useModulePermission(Modules.G204);
  const { mode, setMode, isEditing } = useG204Mode();
  const { names, resolveAllNames, resolveSingleName, clearNames } = useLookupNames();
  const {
    record, formData, details, subDetails, selectedRow, loading,
    setSelectedRow, setDetails, setSubDetails,
    loadRecord, setField, getField,
    handleSave, handleDelete,
    addDetailRow, deleteDetailRow,
    revertToRecord, resetForAdd, applyResult,
    clearFieldErrors,
  } = useG204Record();

  // Wrap loadRecord to also resolve names
  const loadRecordWithNames = useCallback(async (id: string) => {
    const result = await loadRecord(id);
    if (result) {
      resolveAllNames(result.data);
    }
  }, [loadRecord, resolveAllNames]);

  const { allIds, currentIdx, setCurrentIdx, loadList, navTo } = useG204Navigation(loadRecordWithNames);

  // Initial Load
  useEffect(() => {
    (async () => {
      try {
        const ids = await loadList();
        if (ids.length > 0) {
          setCurrentIdx(0);
          await loadRecordWithNames(ids[0]);
        }
      } catch {
        console.error('Failed to load G204 list');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadList, loadRecordWithNames, setCurrentIdx]);

  // Mode Actions
  const handleAddAction = useCallback(async () => {
    if (isEditing) {
      await handleSave(mode, async (result: FG140) => {
        if (mode === 'add') {
          const ids = await loadList();
          const newIdx = ids.indexOf(result.fg140_01);
          setCurrentIdx(newIdx >= 0 ? newIdx : 0);
        }
        applyResult(result);
        setMode('browse');
      });
    } else {
      setMode('add');
      resetForAdd();
      setActiveTab(0);
      clearNames();
    }
  }, [isEditing, mode, handleSave, loadList, setCurrentIdx, applyResult, setMode, resetForAdd, clearNames]);

  const handleModifyAction = useCallback(() => {
    if (isEditing) {
      revertToRecord();
      setMode('browse');
    } else {
      if (!record) return;
      setMode('edit');
    }
  }, [isEditing, record, revertToRecord, setMode]);

  const handleDeleteAction = useCallback(() => {
    if (!record || isEditing) return;
    Modal.confirm({
      title: '確認刪除',
      content: `確定要刪除折讓單 ${record.fg140_01}？`,
      okText: '刪除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        const success = await handleDelete();
        if (success) {
          const ids = await loadList();
          if (ids.length > 0) {
            const nextIdx = Math.min(currentIdx, ids.length - 1);
            setCurrentIdx(nextIdx);
            await loadRecordWithNames(ids[nextIdx]);
          } else {
            resetForAdd();
            setCurrentIdx(-1);
          }
        }
      },
    });
  }, [record, isEditing, handleDelete, loadList, currentIdx, setCurrentIdx, loadRecordWithNames, resetForAdd]);

  const handleNavTo = useCallback((idx: number) => {
    navTo(idx, isEditing);
  }, [navTo, isEditing]);

  const handlePrint = useCallback(() => {
    if (!record || isEditing) return;
    message.info('列印功能待實作');
  }, [record, isEditing]);

  const statusBadge = getStatusBadge(record);
  const queryAcceptanceExact = useCallback((id: string) => g204Api.get(id), []);
  const acceptanceRecords = record ? [record] : [];

  // Render
  return (
    <PermissionGuard module={Modules.G204} action="query">
    <div className="g204-form" data-acceptance-action="PWG204-ACT-LIST">
      {loading && <div className="loading-overlay">載入中...</div>}

      <div className="page-wrapper">
        {/* Page Title Bar */}
        <div className="page-title-bar">
          <div className="page-title-left">
            <nav className="breadcrumb-inline">
              <a href="#/erp" onClick={e => { e.preventDefault(); navigate('/erp'); }}>首頁</a>
              <span>/</span>
              <a href="#/erp" onClick={e => { e.preventDefault(); navigate('/erp'); }}>應收帳款</a>
              <span>/</span>
              <span className="current">銷貨折讓單</span>
            </nav>
            <span className="title-separator">--</span>
            <h1>G204-銷貨折讓單</h1>
            <span className="badge badge-default">G204</span>
            {statusBadge && (
              <span className={`badge ${statusBadge.cls}`}>{statusBadge.text}</span>
            )}
          </div>
        </div>

        {/* Search Bar */}
        <div className="search-bar">
          <label data-contract-field="query_allowance_no">快速查詢</label>
          <input
            data-acceptance-action="PWG204-ACT-GET"
            type="text"
            placeholder="輸入折讓單號"
            disabled={isEditing}
            style={{ width: 160 }}
            onKeyDown={async (e) => {
              if (e.key === 'Enter') {
                const val = (e.target as HTMLInputElement).value.trim();
                if (val) {
                  await loadRecordWithNames(val);
                  const idx = allIds.indexOf(val);
                  if (idx >= 0) setCurrentIdx(idx);
                }
              }
            }}
          />
          <span style={{ fontSize: 12, color: '#999' }}>
            {allIds.length > 0 ? `${currentIdx + 1} / ${allIds.length}` : '無資料'}
          </span>
        </div>

        {/* Tabs */}
        <div className="tabs">
          <div className="tab-nav">
            {['折讓明細', '出貨子明細'].map((label, i) => (
              <button
                key={i}
                className={`tab-nav-item ${activeTab === i ? 'active' : ''}`}
                onClick={() => setActiveTab(i)}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Tab 1: Header + Detail Grid */}
          <div className={`tab-pane ${activeTab === 0 ? 'active' : ''}`}>
            <Tab1Header
              formData={formData}
              names={names}
              isEditing={isEditing}
              getField={getField}
              setField={setField}
              resolveSingleName={resolveSingleName}
            />
            <div style={{ marginTop: 16 }}>
              <Tab1DetailGrid
                details={details}
                selectedRow={selectedRow}
                isEditing={isEditing}
                setSelectedRow={setSelectedRow}
                setDetails={setDetails}
                addDetailRow={addDetailRow}
                deleteDetailRow={deleteDetailRow}
              />
            </div>
          </div>

          {/* Tab 2: Sub-Detail Grid (FG142) */}
          <div className={`tab-pane ${activeTab === 1 ? 'active' : ''}`}>
            <Tab2SubDetailGrid
              details={details}
              subDetails={subDetails}
              selectedRow={selectedRow}
              isEditing={isEditing}
              setSelectedRow={setSelectedRow}
              setSubDetails={setSubDetails}
            />
          </div>
        </div>

        <BatchAcceptanceBridge
          manifest={pwg204f1AcceptanceManifest}
          records={acceptanceRecords}
          selectedKeys={record ? [record.fg140_01] : []}
          keyOf={(item) => item.fg140_01}
          queryExact={queryAcceptanceExact}
          lastApiResponse={record}
          loading={loading}
          onAutoFill={() => { if (record && !isEditing) setMode('edit'); }}
          autoFillDisabled={!record || isEditing}
          autoFillReason="請先載入一張既有折讓單；自動填表只會把目前資料切換為可編輯草稿，不會送出。"
          screenDraft={{ master: formData, details, sub_details: subDetails }}
        />

        {/* Bottom Action Bar (Pwtcmd1) */}
        <ActionBar
          mode={mode}
          isEditing={isEditing}
          record={record}
          currentIdx={currentIdx}
          allIdsLength={allIds.length}
          permissions={perms}
          onAdd={handleAddAction}
          onModify={handleModifyAction}
          onDelete={handleDeleteAction}
          onPrint={handlePrint}
          onNavTo={handleNavTo}
          onExit={() => navigate('/erp')}
        />

      </div>
    </div>
    </PermissionGuard>
  );
};

export default G204Form;
